import * as L from "lucide-react";
import RobotFace from "@/components/RobotFace";

/**
 * ChatWelcome — the empty-state hero on a fresh chat.
 *
 * Replaces the previous single-line greeting that made the workspace feel
 * unfinished. Fills the empty canvas with:
 *   • A larger, breathing Rammy face with an ambient glow
 *   • A warm greeting keyed to time-of-day
 *   • Four quick-action tiles that are the fastest paths to value:
 *       "Brief my day", "Set a reminder", "Draft an email reply",
 *       "Create an image"
 *   • A voice-mode entry point
 *
 * Kept purely presentational — all handlers are passed in.
 */
export default function ChatWelcome({ user, expression, onQuickAction, onOpenVoice }) {
  const firstName = user?.preferred_name || user?.name?.split(" ")[0] || "there";
  const robotName = "Rammy";
  const hour = new Date().getHours();
  const greet =
    hour < 5  ? "Late night"
    : hour < 12 ? "Good morning"
    : hour < 17 ? "Good afternoon"
    : hour < 21 ? "Good evening"
    : "Winding down";

  const tiles = [
    {
      key: "brief",
      icon: <L.Sparkles size={16}/>,
      title: "Brief my day",
      hint: "A short, warm read-out from your calendar",
      text: "Give me a warm, short briefing of my day using my calendar.",
      gradient: "from-[var(--rammy-cyan)]/25 to-[var(--rammy-cyan)]/5",
    },
    {
      key: "reminder",
      icon: <L.BellRing size={16}/>,
      title: "Set a reminder",
      hint: "Try: 'Remind me to call mom at 5pm'",
      text: "Remind me to take a walk in 20 minutes.",
      gradient: "from-amber-200/40 to-amber-50",
    },
    {
      key: "email",
      icon: <L.Mail size={16}/>,
      title: "Draft an email reply",
      hint: "I'll read your inbox and draft the reply",
      text: "Check my unread emails and help me triage.",
      gradient: "from-violet-200/40 to-violet-50",
    },
    {
      key: "image",
      icon: <L.Image size={16}/>,
      title: "Create an image",
      hint: "Describe a scene — I'll draw it",
      text: "Design a warm, minimal wordmark for my coffee shop called Ember.",
      gradient: "from-rose-200/40 to-rose-50",
    },
  ];

  return (
    <div className="relative flex flex-col items-center justify-center py-6 md:py-10 px-2" data-testid="chat-welcome">
      {/* Ambient background glow — soft, warm, expression-tinted */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden"
      >
        <div className={`w-[520px] h-[520px] rounded-full blur-3xl opacity-40 ${
          expression === "sad" ? "bg-rose-200"
          : expression === "thinking" ? "bg-amber-200"
          : "bg-[var(--rammy-cyan)]/40"
        }`}/>
      </div>

      {/* Rammy signature form — warm ivory squircle with a soft turquoise
          glow orb + tiny inner core. Reads as alive and calmly intelligent,
          not robotic. Scales up naturally from the shared .rammy-orb tokens. */}
      <div className="relative z-10 mb-6">
        <div className={`absolute inset-0 rounded-[2rem] blur-3xl transition-all duration-1000 ${
          expression === "speaking" ? "bg-[var(--rammy-cyan)]/28 scale-125"
          : "bg-[var(--rammy-cyan)]/16 scale-110 animate-pulse"
        }`}/>
        <div
          className="relative rammy-orb rammy-orb--lg"
          data-mood={expression === "speaking" ? "speaking" : expression === "thinking" ? "thinking" : expression === "sad" ? "sad" : "idle"}
        />
      </div>

      {/* Greeting */}
      <div className="relative z-10 text-center max-w-[560px]">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--rammy-cyan-dark)] font-semibold">
          {greet}, {firstName}
        </div>
        <h1 className="mt-2 text-2xl md:text-3xl font-display text-[var(--rammy-text)] leading-tight" data-testid="chat-welcome-title">
          What's on your mind?
        </h1>
        <p className="mt-2 text-[13px] md:text-[14px] text-[var(--rammy-text-sec)] leading-relaxed">
          I'm {robotName} — your companion for the everyday. Talk to me like a
          friend. I'll remember what matters, keep your day flowing, and help
          you make things.
        </p>
      </div>

      {/* Voice mode CTA — the most-differentiated interaction */}
      <button
        type="button"
        onClick={onOpenVoice}
        data-testid="chat-welcome-voice"
        className="relative z-10 mt-4 inline-flex items-center gap-2 px-4 h-10 rounded-full bg-[var(--rm-ink-900)] text-white text-[13px] font-medium hover:opacity-90 transition shadow-[0_10px_30px_-10px_rgba(0,0,0,0.35)] group"
      >
        <span className="w-6 h-6 rounded-full bg-[var(--rammy-cyan)]/25 flex items-center justify-center">
          <L.Mic size={13} className="text-[var(--rammy-cyan)]"/>
        </span>
        Talk instead — voice mode
        <L.ArrowRight size={13} className="opacity-60 group-hover:translate-x-0.5 transition-transform"/>
      </button>

      {/* Quick actions grid */}
      <div className="relative z-10 mt-8 w-full max-w-[720px]" data-testid="chat-welcome-tiles">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--rammy-text-muted)] text-center font-semibold mb-3">
          Or try one of these
        </div>
        <div className="grid grid-cols-2 gap-3">
          {tiles.map((t) => (
            <button
              key={t.key}
              onClick={() => onQuickAction(t.text)}
              data-testid={`chat-welcome-tile-${t.key}`}
              className={`group text-left rounded-2xl bg-gradient-to-br ${t.gradient} border border-white/60 backdrop-blur p-4 hover:border-[var(--rammy-cyan)]/50 hover:scale-[1.015] transition-all shadow-[0_2px_10px_rgba(0,0,0,0.04)]`}
            >
              <div className="flex items-center gap-2 text-[var(--rammy-text)]">
                <span className="w-8 h-8 rounded-xl bg-[var(--rm-surface)]/70 flex items-center justify-center text-[var(--rammy-cyan-dark)] group-hover:bg-[var(--rm-surface)] transition">
                  {t.icon}
                </span>
                <span className="text-[14px] font-semibold">{t.title}</span>
              </div>
              <div className="text-[12px] text-[var(--rammy-text-sec)] mt-1.5 leading-snug">
                {t.hint}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
